import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useVariances } from '../lib/useReconciliation';
import { useWeeklyRollups } from '../lib/useRollups';
import {
  Container,
  Dropdown,
  EmptyState,
  FilterBar,
  FilterBarRight,
  FilterBarSpacer,
  FilterChip,
  PageHead,
  Stat,
  StatRow,
  StatusPill,
  type StatusPillKind,
} from '../components/design';
import { Scale } from 'lucide-react';
import type { VarianceStatusFilter } from '../lib/api';
import { formatDateShort, formatMoneyCents, formatPcoNumber, formatSubmissionLabel, gcName } from '../lib/format';
import type { RollupListItem, VarianceRecord, VarianceStatus } from '@coffman-tm/shared';

const FILTERS: ReadonlyArray<{ value: VarianceStatusFilter; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'disputed', label: 'Disputed' },
  { value: 'resolved_recovered', label: 'Recovered' },
  { value: 'resolved_accepted', label: 'Accepted' },
  { value: 'all', label: 'All' },
];

export function ReconciliationPage() {
  const { me } = useAuth();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const [filter, setFilter] = useState<VarianceStatusFilter>(
    // When deep-linked to a specific variance, start on "All" so the focused
    // row is guaranteed to be rendered regardless of its current status.
    focusId ? 'all' : 'open',
  );
  const { data, isLoading, refetch } = useVariances(filter);
  // Memoized so the `?? []` fallback doesn't hand a fresh array to the grouped
  // useMemo (below) on every render when data is still loading.
  const variances = useMemo(() => data?.variances ?? [], [data]);
  const rollups = useWeeklyRollups();

  // Scroll the focused variance row into view + flash it once the data
  // arrives. Deep-link entry points (PcoNeedsAttention's per-variance
  // Resolve link) rely on this.
  useEffect(() => {
    if (!focusId || !data) return;
    const el = document.getElementById(`variance-${focusId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('row-flash');
    const t = window.setTimeout(() => el.classList.remove('row-flash'), 2200);
    return () => window.clearTimeout(t);
  }, [focusId, data]);

  // Group by PCO for the recon-group bands.
  const grouped = useMemo(() => {
    const m = new Map<string, { pco: VarianceRecord['pco']; rows: VarianceRecord[] }>();
    for (const v of variances) {
      const existing = m.get(v.pco.id);
      if (existing) existing.rows.push(v);
      else m.set(v.pco.id, { pco: v.pco, rows: [v] });
    }
    return [...m.values()];
  }, [variances]);

  // Stat-row aggregates — all-time across whatever the current filter shows.
  const openCount = variances.filter((v) => v.status === 'open' || v.status === 'investigating').length;
  const disputedCount = variances.filter((v) => v.status === 'disputed').length;
  const disputedCents = variances
    .filter((v) => v.status === 'disputed')
    .reduce((sum, v) => sum + Math.abs(v.variance_cents), 0);
  const resolvedCount = variances.filter(
    (v) => v.status === 'resolved_recovered' || v.status === 'resolved_accepted',
  ).length;
  const lastDiscovered = variances
    .map((v) => v.discovered_at)
    .sort()
    .pop();

  return (
    <Container>
      <PageHead
        eyebrow="Reconciliation"
        title="Where the books don't agree"
        sub={
          isLoading
            ? 'Loading…'
            : variances.length === 0
              ? filter === 'open'
                ? 'No open variances. All recorded amounts match.'
                : `No variances with status "${filter}".`
              : `${variances.length} variance${variances.length === 1 ? '' : 's'} in this view.`
        }
      />

      <StatRow>
        <Stat
          label="Open + investigating"
          value={openCount === 0 ? '—' : String(openCount)}
        />
        <Stat
          label="In dispute"
          value={
            disputedCount === 0 ? (
              '—'
            ) : (
              <span style={{ color: 'var(--danger)' }}>{formatMoneyCents(disputedCents)}</span>
            )
          }
          meta={
            disputedCount > 0
              ? `${disputedCount} record${disputedCount === 1 ? '' : 's'}`
              : null
          }
        />
        <Stat label="Resolved" value={resolvedCount === 0 ? '—' : String(resolvedCount)} />
        <Stat
          label="Last discovered"
          value={lastDiscovered ? formatDateShort(lastDiscovered.slice(0, 10)) : '—'}
        />
      </StatRow>

      <RecordVarianceForm
        availableRollups={rollups.data?.rollups ?? []}
        onCreated={refetch}
      />

      <FilterBar>
        {FILTERS.map((f) => (
          <FilterChip
            key={f.value}
            active={filter === f.value}
            count={filter === f.value && data ? variances.length : undefined}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </FilterChip>
        ))}
        <FilterBarSpacer />
        <FilterBarRight>
          <span className="eyebrow">Newest first</span>
        </FilterBarRight>
      </FilterBar>

      {grouped.length === 0 ? (
        <EmptyState
          eyebrow="Variances"
          icon={Scale}
          text={
            filter === 'open'
              ? `Nothing to reconcile right now. New variances appear when ${gcName(me)}'s recorded amount differs from what Coffman billed.`
              : `No variances in this view.`
          }
        />
      ) : (
        grouped.map((g) => <ReconGroup key={g.pco.id} pco={g.pco} rows={g.rows} />)
      )}
    </Container>
  );
}

/**
 * Form to record a new variance. Joey picks a submitted rollup from the
 * dropdown, enters what Hoffman recorded, optional notes; server computes
 * coffman_amount + variance from the rollup's invoice_total_cents.
 */
function RecordVarianceForm({
  availableRollups,
  onCreated,
}: {
  availableRollups: RollupListItem[];
  onCreated: () => Promise<void>;
}) {
  const { token, me } = useAuth();
  const [open, setOpen] = useState(false);
  const [rollupId, setRollupId] = useState<string>('');
  const [hoffmanDollars, setHoffmanDollars] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setRollupId('');
    setHoffmanDollars('');
    setNotes('');
    setErr(null);
  };

  const rollupOptions = useMemo(
    () => [
      { value: '', label: 'Select a rollup…' },
      ...availableRollups.map((r) => ({
        value: r.id,
        label: `PCO ${formatPcoNumber(r.pco.pco_number)} · wk ${r.week_ending_date} · ${formatMoneyCents(r.totals.invoice_total_cents)}`,
      })),
    ],
    [availableRollups],
  );

  const submit = async () => {
    if (!token) return;
    if (!rollupId) {
      setErr('Pick a rollup.');
      return;
    }
    const dollars = Number(hoffmanDollars);
    if (!Number.isFinite(dollars)) {
      setErr(`Enter ${gcName(me)}'s amount in dollars (e.g. 12500.50).`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.reconciliation.create(token, {
        weekly_rollup_id: rollupId,
        hoffman_amount_cents: Math.round(dollars * 100),
        ...(notes.trim() ? { resolution_notes: notes.trim() } : {}),
      });
      reset();
      setOpen(false);
      await onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '24px 0 8px' }}>
        <button
          type="button"
          className="btn btn-cta"
          onClick={() => setOpen(true)}
          disabled={availableRollups.length === 0}
        >
          + Record GC amount
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        margin: '24px 0 8px',
        padding: '20px 24px',
        background: 'var(--surface-warm)',
        border: '1px solid var(--rule-strong)',
      }}
    >
      <div className="serif-h2" style={{ marginBottom: 14, fontSize: 20 }}>
        Record what the GC <em>actually paid.</em>
      </div>

      <div className="settings-grid" style={{ borderTop: 'none', marginTop: 8 }}>
        <div>
          <div className="field-row">
            <div className="fr-label">Rollup</div>
            <div className="fr-value" style={{ textAlign: 'left' }}>
              <Dropdown
                style={{ width: '100%' }}
                options={rollupOptions}
                value={rollupId}
                onChange={setRollupId}
                disabled={busy}
              />
            </div>
          </div>
          <div className="field-row">
            <div className="fr-label">GC amount ($)</div>
            <div className="fr-value">
              <input
                type="number"
                step="0.01"
                value={hoffmanDollars}
                onChange={(e) => setHoffmanDollars(e.target.value)}
                placeholder="12500.50"
                disabled={busy}
                style={{
                  width: '100%',
                  fontFamily: 'var(--mono)',
                  fontSize: 13,
                  textAlign: 'right',
                  padding: '4px 0',
                  border: 'none',
                  borderBottom: '1px solid var(--rule-strong)',
                  background: 'transparent',
                  color: 'var(--ink)',
                }}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="field-row">
            <div className="fr-label">Notes (optional)</div>
            <div className="fr-value" style={{ textAlign: 'left' }}>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Where the gap came from, who flagged it, etc."
                disabled={busy}
                rows={3}
                style={{
                  width: '100%',
                  fontFamily: 'var(--sans)',
                  fontSize: 13,
                  padding: 4,
                  border: '1px solid var(--rule)',
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  resize: 'vertical',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 8,
          marginTop: 12,
        }}
      >
        {err && (
          <span style={{ color: 'var(--danger)', fontSize: 12, marginRight: 'auto' }}>{err}</span>
        )}
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
          disabled={busy}
        >
          Cancel
        </button>
        <button type="button" className="btn btn-cta" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : 'Record variance'}
        </button>
      </div>
    </div>
  );
}

function ReconGroup({
  pco,
  rows,
}: {
  pco: VarianceRecord['pco'];
  rows: VarianceRecord[];
}) {
  const total = rows.reduce((sum, r) => sum + r.variance_cents, 0);
  return (
    <div className="recon-group">
      <div className="recon-head">
        <div>
          <div className="rh-id">PCO {formatPcoNumber(pco.pco_number)}</div>
          <div className="rh-name">{pco.description}</div>
        </div>
        <div />
        <div className={`rh-variance ${total < 0 ? 'neg' : total > 0 ? 'pos' : ''}`}>
          <span className="lbl">Group total</span>
          {formatMoneyCents(total, { precise: true })}
        </div>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Line / Week</th>
            <th className="num">Coffman invoiced</th>
            <th className="num">GC approved</th>
            <th className="num">Variance</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} id={`variance-${r.id}`}>
              <td className="muted">
                {r.weekly_rollup
                  ? `${formatSubmissionLabel(pco.pco_number, r.weekly_rollup.sub_pco_number)} · week ending ${formatDateShort(r.weekly_rollup.week_ending_date)}`
                  : 'Manual entry'}
              </td>
              <td className="num">{formatMoneyCents(r.coffman_amount_cents, { precise: true })}</td>
              <td className="num">{formatMoneyCents(r.hoffman_amount_cents, { precise: true })}</td>
              <td
                className="num"
                style={
                  r.variance_cents !== 0
                    ? { color: r.variance_cents > 0 ? 'var(--positive)' : 'var(--danger)' }
                    : undefined
                }
              >
                {formatMoneyCents(r.variance_cents, { precise: true })}
              </td>
              <td>
                <StatusPill kind={pillKindFor(r.status)}>{statusLabel(r.status)}</StatusPill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function pillKindFor(status: VarianceStatus): StatusPillKind {
  switch (status) {
    case 'open':
      return 'pending';
    case 'investigating':
      return 'progress';
    case 'disputed':
      return 'danger';
    case 'resolved_recovered':
      return 'ready';
    case 'resolved_accepted':
      return 'neutral';
  }
}

function statusLabel(status: VarianceStatus): string {
  switch (status) {
    case 'open':
      return 'Open';
    case 'investigating':
      return 'Investigating';
    case 'disputed':
      return 'Disputed';
    case 'resolved_recovered':
      return 'Recovered';
    case 'resolved_accepted':
      return 'Accepted';
  }
}
